/**
 * CERTIFICATIONS — what the shop can honestly claim, and when it stops
 * being true.
 *
 * ASE tickets run five years and lapse per area, not all at once. A shop
 * whose website says "ASE certified technicians" with three expired
 * certificates has a marketing problem today and a much bigger one the
 * first time something goes wrong on a job.
 *
 * SORTED BY WHAT EXPIRES SOONEST, not alphabetically. The only question
 * anybody opens this screen to answer is "what do I need to renew", and a
 * list in name order makes you read all of it to find out.
 *
 * A technician sees their own and nothing else — the same rule as My pay,
 * and honest about being an interface rule rather than a database one.
 */

import { db } from "../data/client.ts";
import { technicians, meAsTech, type Tech } from "../data/pay.ts";
import { esc, mount } from "../ui/dom.ts";
import { TRACKS, CERT_BODY, trackFor, studyUrl, examUrl } from "../domain/cert-tracks.ts";
import type { Role } from "../domain/permissions.ts";

const BOSS: Role[] = ["admin", "supervisor"];
const DAY = 86400000;

type Cert = {
  // Exactly one owner: a wrench record, or a person with a login. 0013
  // added the second so an advisor could hold a ticket without being put
  // in every "who is doing this job" dropdown in the system.
  id: string; technician_id: string | null; profile_id: string | null;
  body: string; name: string;
  reference: string | null; earned_on: string | null; expires_on: string | null; note: string | null;
};

/** How a certificate stands today, in the words a shop would use. */
function standing(c: Cert): { cls: string; says: string } {
  if (!c.expires_on) return { cls: "ok", says: "no expiry recorded" };
  const days = Math.round((new Date(c.expires_on).getTime() - Date.now()) / DAY);
  if (days < 0) return { cls: "gone", says: `expired ${Math.abs(days)} days ago` };
  if (days <= 60) return { cls: "soon", says: `expires in ${days} days` };
  return { cls: "ok", says: `good until ${new Date(c.expires_on).toLocaleDateString("en-US",
    { month: "short", year: "numeric" })}` };
}

/** Expired first, then expiring soonest. Undated last — it asks nothing. */
const order = (a: Cert, b: Cert) => {
  if (!a.expires_on) return 1;
  if (!b.expires_on) return -1;
  return a.expires_on.localeCompare(b.expires_on);
};

/**
 * NOTHING ON FILE IS NOT A DEAD END.
 *
 * Anthony: "there are only pages for Mike, Alaina and Anthony right now.
 * Others if they go to the page send them to get certified at
 * mechanicrank.com."
 *
 * Two people in this shop hold certificates. The other thirty-one opened
 * this screen and read "Nothing recorded yet", which tells somebody they
 * have nothing and not one thing about how to stop having nothing. The
 * exams are sixty questions and free to sit, so the only thing standing
 * between a technician and a ticket was a link.
 *
 * It is deliberately not scolding. A technician with no certificate is not
 * doing anything wrong, and a screen that implies otherwise is a screen
 * people stop opening.
 */
function getCertified(why: string): string {
  return `
    <div class="card pad certgo">
      <div class="certgo-h">Get certified</div>
      <p>${esc(why)}</p>
      <p class="quiet">MechanicRank runs thirteen tracks, sixty questions each. A pass is
        recorded here automatically and shows on your profile.</p>
      <a class="btn primary" href="${esc(examUrl())}" target="_blank" rel="noopener">
        Sit an exam at mechanicrank.com</a>
    </div>`;
}

export async function certsPage(role: Role = "viewer"): Promise<void> {
  const boss = BOSS.includes(role);

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Certifications</h1>
      <p>${boss
        ? "What the shop can honestly claim, and what is about to stop being true."
        : "Your tickets, and when they run out."}</p></header>
    ${boss ? `<button class="btn primary" id="add-cert">Add a certification</button>` : ""}
    <div id="certs"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  const host = document.getElementById("certs");
  if (!host) return;

  try {
    const sb = await db();
    const me = boss ? null : await meAsTech();

    if (!boss && !me) {
      host.innerHTML = getCertified(
        "Your login is not linked to a technician record yet, so there is nothing "
        + "on file to show. An admin can link it on the Team screen.")
        + `<div class="card pad"><p class="quiet">Once it is linked, anything you pass
             shows up here on its own.</p></div>`;
      return;
    }

    let q = sb.from("certifications")
      .select("id,technician_id,profile_id,body,name,reference,earned_on,expires_on,note")
      .limit(500);
    if (me) q = q.eq("technician_id", me.id);

    const [certRes, techs, people] = await Promise.all([
      q, technicians(),
      sb.from("profiles").select("id,full_name"),
    ]);
    if (certRes.error) throw new Error(certRes.error.message);

    const certs = ((certRes.data ?? []) as unknown as Cert[]).sort(order);
    const nameOf = new Map<string, string>(techs.map((t) => [t.id, t.name]));
    for (const p of (people.data ?? []) as Array<{ id: string; full_name: string }>) {
      nameOf.set(p.id, p.full_name);
    }
    /** Whoever it belongs to — a technician record or a login. */
    const whose = (c: Cert) => nameOf.get(c.technician_id ?? c.profile_id ?? "") ?? "Unknown";

    const expired = certs.filter((c) => standing(c).cls === "gone").length;
    const soon = certs.filter((c) => standing(c).cls === "soon").length;

    host.innerHTML = `
      ${certs.length ? "" : getCertified(boss
        ? "Nobody at the shop has a certificate on file yet."
        : "You have no certificates on file yet.")}
      ${certs.length ? `
        <div class="mtiles">
          <div class="mtile count"><div class="mtile-l">On file</div>
            <div class="mtile-v">${certs.length}</div>
            <div class="mtile-n">across ${
              new Set(certs.map((c) => c.technician_id ?? c.profile_id)).size} people</div></div>
          <div class="mtile ${expired ? "rate" : "count"}"><div class="mtile-l">Expired</div>
            <div class="mtile-v">${expired}</div>
            <div class="mtile-n">${expired ? "cannot be claimed today" : "nothing lapsed"}</div></div>
          <div class="mtile count"><div class="mtile-l">Due within 60 days</div>
            <div class="mtile-v">${soon}</div>
            <div class="mtile-n">book the renewal now</div></div>
        </div>` : ""}

      <!-- THE TRACKS THEMSELVES. A shop wants to know what its people
           could hold, not only what they do — and a technician wants
           somewhere to go next. Passed ones are marked from the
           certifications already on file. -->
      <div class="card">
        <div class="card-head">MechanicRank tracks &middot; ${
          certs.filter((c) => trackFor(c.body, c.name)).length} of ${TRACKS.length} held</div>
        <div class="ctracks">
          ${TRACKS.map((t) => {
            const held = certs.filter((c) => trackFor(c.body, c.name)?.code === t.code);
            const who = held.map(whose).filter((n) => n !== "Unknown");
            return `
              <div class="ctrack ${held.length ? "held" : ""}">
                <div class="ctrack-t"><span class="ctrack-code">${esc(t.code)}</span>
                  ${esc(t.title)}</div>
                <div class="ctrack-w">${esc(t.what)}</div>
                ${who.length
                  ? `<div class="ctrack-who">${esc([...new Set(who)].join(", "))}</div>`
                  : `<div class="ctrack-none">Nobody here holds this yet</div>`}
                <a class="ctrack-go" href="${esc(studyUrl(t))}" target="_blank"
                   rel="noopener">Study it &rarr;</a>
              </div>`;
          }).join("")}
        </div>
        <div class="pad">
          <p class="quiet">Sixty questions each, taken on
            <a href="${esc(examUrl())}" target="_blank" rel="noopener">MechanicRank</a>.
            A pass is recorded here as ${esc(CERT_BODY)} plus the code.</p>
        </div>
      </div>

      <div class="card">
        <div class="card-head">Everything on file, soonest to expire first</div>
        ${certs.length ? certs.map((c) => {
          const s = standing(c);
          return `
            <div class="row cert ${s.cls}">
              <div class="row-main">
                <div class="row-title">
                  <span class="num">${esc(c.body)} ${esc(c.name)}</span>
                  ${boss ? `<span class="pill">${esc(whose(c))}</span>` : ""}
                </div>
                <div class="row-sub">${esc(s.says)}${
                  c.reference ? `<span class="dot">&middot;</span>${esc(c.reference)}` : ""}${
                  trackFor(c.body, c.name)
                    ? `<span class="dot">&middot;</span>${esc(trackFor(c.body, c.name)!.title)}` : ""}</div>
                ${c.note ? `<div class="row-note">${esc(c.note)}</div>` : ""}
              </div>
              ${boss ? `<div class="row-side">
                <button class="btn ghost sm" data-del="${esc(c.id)}">Remove</button></div>` : ""}
            </div>`;
        }).join("") : `<div class="empty">Nothing recorded yet.</div>`}
      </div>`;

    host.querySelectorAll<HTMLButtonElement>("[data-del]").forEach((b) =>
      b.addEventListener("click", async () => {
        if (b.dataset["sure"] !== "1") { b.dataset["sure"] = "1"; b.textContent = "Sure?"; return; }
        const sb2 = await db();
        await sb2.from("certifications").delete().eq("id", b.dataset["del"]);
        void certsPage(role);
      }));

    document.getElementById("add-cert")?.addEventListener("click", () => addForm(role, techs));
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}

function addForm(role: Role, techs: Tech[]): void {
  document.getElementById("certsheet")?.remove();
  document.body.insertAdjacentHTML("beforeend", `
    <div class="sheet-back" id="certsheet">
      <div class="sheet" role="dialog" aria-modal="true">
        <div class="sheet-head"><div><h2>Add a certification</h2></div>
          <button class="sheet-x" id="cs-x" aria-label="Close">&times;</button></div>
        <div class="sheet-body">
          <label class="field"><span>Who</span><select id="c-tech">
            ${techs.filter((t) => t.active).map((t) =>
              `<option value="${esc(t.id)}">${esc(t.name)}</option>`).join("")}
          </select></label>
          <label class="field"><span>Awarded by</span>
            <input id="c-body" list="cert-bodies">
            <datalist id="cert-bodies"><option value="${esc(CERT_BODY)}"><option value="ASE">
              <option value="EPA"></datalist></label>
          <label class="field"><span>What it is</span>
            <input id="c-name" list="cert-names">
            <datalist id="cert-names">${TRACKS.map((t) =>
              `<option value="${esc(t.code)}">${esc(t.title)}`).join("")}</datalist></label>
          <p class="quiet small">For a MechanicRank track, put ${esc(CERT_BODY)} and the
             three-letter code &mdash; then it links to the study page.</p>
          <label class="field"><span>Certificate number</span>
            <input id="c-ref"></label>
          <label class="field"><span>Earned</span><input id="c-earned" type="date"></label>
          <label class="field"><span>Expires</span><input id="c-expires" type="date"></label>
          <p class="quiet small">Leave the expiry blank only if it genuinely never lapses.
             A blank date will never warn you.</p>
        </div>
        <div class="sheet-acts">
          <button class="btn primary" id="cs-save">Add it</button>
          <button class="btn ghost" id="cs-cancel">Cancel</button>
        </div>
        <div class="msg-slot" id="cs-msg"></div>
      </div>
    </div>`);

  const shut = () => document.getElementById("certsheet")?.remove();
  document.getElementById("cs-x")?.addEventListener("click", shut);
  document.getElementById("cs-cancel")?.addEventListener("click", shut);

  const v = (id: string) =>
    (document.getElementById(id) as HTMLInputElement | HTMLSelectElement | null)?.value.trim() ?? "";

  document.getElementById("cs-save")?.addEventListener("click", async () => {
    const b = document.getElementById("cs-save") as HTMLButtonElement;
    const msg = document.getElementById("cs-msg");
    b.disabled = true; b.textContent = "Saving…";
    try {
      if (!v("c-body") || !v("c-name")) throw new Error("It needs an awarding body and a name.");
      const sb = await db();
      const { data: me } = await sb.auth.getUser();
      const { data: prof } = await sb.from("profiles")
        .select("account_id").eq("id", me.user?.id ?? "").maybeSingle();
      const accountId = (prof as { account_id: string } | null)?.account_id;
      if (!accountId) throw new Error("Cannot tell which shop you belong to.");

      const { error } = await sb.from("certifications").insert({
        account_id: accountId, technician_id: v("c-tech"),
        body: v("c-body"), name: v("c-name"),
        reference: v("c-ref") || null,
        earned_on: v("c-earned") || null,
        expires_on: v("c-expires") || null,
      });
      if (error) throw new Error(error.message);
      shut(); void certsPage(role);
    } catch (e) {
      b.disabled = false; b.textContent = "Add it";
      if (msg) {
        msg.innerHTML = `<p class="msg bad">${esc(e instanceof Error ? e.message : String(e))}</p>`;
      }
    }
  });
}
